import { watch, type Ref } from "vue";
import type { GeoJSONSource, Map as MapboxMap } from "mapbox-gl";
import type { FeatureCollection } from "geojson";
import { bindMapPinImage, mapPinSymbolLayout } from "../utils/mapPinImage";
import { EMPTY_FEATURE_COLLECTION, removeLayersIfPresent, removeSourcesIfPresent, useAppMapStyleLayers } from "./useAppMapStyleLayers";

const SOURCE_ID = "active-pin";
const LAYER_ID = "active-pin-symbol";
const IMAGE_ID = "active-pin-image";

interface UseAppMapActivePinOptions {
  data: Ref<FeatureCollection | undefined>;
  activeFeatureId: Ref<number | string | undefined>;
  imageUrl: string;
  width: number;
  // Le point actif éclaté par l'escargot est rendu à sa patte, pas à ses coordonnées réelles.
  positionOverrides?: Ref<Map<string, [number, number]>>;
  // Zoom à partir duquel la source des groupes prend le relais : elle seule sait si le point actif
  // y survit isolé, méritant son marqueur, ou s'il est absorbé par un groupe qui portera lui-même
  // la sélection. Le relais passe par une expression, donc au zoom exact où le style bascule — un
  // seuil évalué en JavaScript laisserait les deux se chevaucher sur une plage de zoom.
  hiddenFromZoom?: number;
}

export function useAppMapActivePin(map: Ref<MapboxMap | undefined>, options: UseAppMapActivePinOptions) {
  const pin = { id: IMAGE_ID, url: options.imageUrl, width: options.width };

  function activeCollection(): FeatureCollection {
    const id = options.activeFeatureId.value;
    const fc = options.data.value;
    if (id == null || !fc) return EMPTY_FEATURE_COLLECTION;
    const feature = fc.features.find((f) => f.geometry.type === "Point" && String(f.properties?.id) === String(id));
    if (!feature) return EMPTY_FEATURE_COLLECTION;
    const override = options.positionOverrides?.value.get(String(id));
    const positioned = override ? { ...feature, geometry: { type: "Point" as const, coordinates: override } } : feature;
    return { type: "FeatureCollection", features: [positioned] };
  }

  function applyActive(m: MapboxMap) {
    const source = m.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    if (source) source.setData(activeCollection());
  }

  // Éclaté, le point a quitté la source des groupes : son marqueur reste à la charge de ce calque,
  // quel que soit le zoom. L'exception est portée par l'expression elle-même plutôt que par une
  // condition sur la feature : mêlée au zoom, celle-ci en ferait une fonction composite, que Mapbox
  // interpole entre les deux zooms entiers encadrants — le relais se ferait alors en fondu.
  function opacityExpression() {
    if (options.hiddenFromZoom == null) return 1;
    const id = options.activeFeatureId.value;
    const spiderfied = id != null && options.positionOverrides?.value.has(String(id));
    return spiderfied ? 1 : ["step", ["zoom"], 1, options.hiddenFromZoom, 0];
  }

  function applyOpacity(m: MapboxMap) {
    if (m.getLayer(LAYER_ID)) m.setPaintProperty(LAYER_ID, "icon-opacity", opacityExpression() as never);
  }

  function setup(m: MapboxMap) {
    const unbindImage = bindMapPinImage(m, pin);

    m.addSource(SOURCE_ID, { type: "geojson", data: activeCollection() });
    m.addLayer({
      id: LAYER_ID,
      type: "symbol",
      source: SOURCE_ID,
      layout: mapPinSymbolLayout(IMAGE_ID),
      paint: {
        "icon-opacity": opacityExpression() as never,
        "icon-opacity-transition": { duration: 0 },
      },
    });

    return () => {
      unbindImage();
      removeLayersIfPresent(m, LAYER_ID);
      removeSourcesIfPresent(m, SOURCE_ID);
    };
  }

  useAppMapStyleLayers(map, setup);

  watch([options.data, options.activeFeatureId, () => options.positionOverrides?.value], () => {
    const m = map.value;
    if (!m) return;
    applyActive(m);
    applyOpacity(m);
  });
}
